import React from "react";
import { ChevronDown, Database } from "react-feather";
import { KgInspectorPathNavLinkRow } from "../../atoms/KgInspectorPathNavLinkRow";
import { kgInspectorIcons } from "../../kg/canvas/inspector/kgInspectorIcons";
import {
  Chevron,
  CountBadge,
  DataSourcesHeader,
  DataSourcesSection,
  DescriptionBody,
  EmptyHint,
  IconSlot,
  LabelGroup,
  LinkRowStack,
  SectionBlock,
  SectionHeader,
  SectionTitle,
  TabRoot,
} from "./KgInspectorOntologyOverview.styles";
import type { KgInspectorOntologyOverviewProps } from "./KgInspectorOntologyOverview.types";

const DescriptionIcon = kgInspectorIcons.description;

export const KgInspectorOntologyOverview: React.FC<
  KgInspectorOntologyOverviewProps
> = ({
  overview,
  isDataSourcesOpen = true,
  onToggleDataSources,
  descriptionEmptyHint = "No description available for this ontology yet.",
  dataSourcesEmptyHint = "No data sources linked to this ontology yet.",
  showDataSources = true,
}) => {
  const description = overview?.description ?? "";
  const dataSources = overview?.dataSources ?? [];

  const toggleProps = onToggleDataSources
    ? {
        role: "button" as const,
        "aria-expanded": isDataSourcesOpen,
        onClick: onToggleDataSources,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggleDataSources();
          }
        },
        tabIndex: 0,
      }
    : {};

  const dataSourcesHeader = (
    <>
      <LabelGroup>
        <IconSlot>
          <Database size={16} strokeWidth={1.75} />
        </IconSlot>
        <SectionTitle>Data sources</SectionTitle>
        {dataSources.length > 0 && (
          <CountBadge>{dataSources.length}</CountBadge>
        )}
      </LabelGroup>
      {onToggleDataSources && (
        <Chevron $isOpen={isDataSourcesOpen} aria-hidden>
          <ChevronDown size={20} />
        </Chevron>
      )}
    </>
  );

  return (
    <TabRoot>
      <SectionBlock>
        <SectionHeader>
          <IconSlot>
            <DescriptionIcon size={16} />
          </IconSlot>
          <SectionTitle>Description</SectionTitle>
        </SectionHeader>
        {description ? (
          <DescriptionBody>{description}</DescriptionBody>
        ) : (
          <EmptyHint>{descriptionEmptyHint}</EmptyHint>
        )}
      </SectionBlock>

      {showDataSources && (
        <DataSourcesSection
          $isOpen={isDataSourcesOpen}
          {...(!isDataSourcesOpen ? toggleProps : {})}
        >
          {isDataSourcesOpen ? (
            <>
              <DataSourcesHeader {...toggleProps}>{dataSourcesHeader}</DataSourcesHeader>
              {dataSources.length === 0 ? (
                <EmptyHint>{dataSourcesEmptyHint}</EmptyHint>
              ) : (
                <LinkRowStack>
                  {dataSources.map((item) => (
                    <KgInspectorPathNavLinkRow
                      key={`${item.label}-${item.dataSourceName}`}
                      link={item}
                      pathSuffix={item.dataSourceName}
                    />
                  ))}
                </LinkRowStack>
              )}
            </>
          ) : (
            dataSourcesHeader
          )}
        </DataSourcesSection>
      )}
    </TabRoot>
  );
};

export default KgInspectorOntologyOverview;
